import { forwardRef, useCallback, Ref, RefAttributes, type JSX } from "react";

import { Select, useTheme, SelectChangeEvent } from "@mui/material";
import { FieldValues, Path, PathValue, useController } from "react-hook-form";

import { BaseFormSelectFieldProps, FormSelectFieldProps, SubscribedFormSelectFieldProps } from "./types";

import { FormField, getFormFieldProps } from "@/forms-design-system/FormField";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { mergeSx } from "@/forms-design-system/helpers";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { useValidators } from "@/hooks/useValidators";
import { ChevronDown, Cross } from "@/icons/outlined";

function FormSelectFieldInner<T extends FieldValues>(
  { control, rules, name, shouldUnregister, ...rest }: FormSelectFieldProps<T>,
  ref: Ref<HTMLDivElement>
) {
  return control && name ? (
    <SubscribedFormSelectField
      {...rest}
      ref={ref}
      control={control}
      rules={rules}
      name={name}
      shouldUnregister={shouldUnregister}
    />
  ) : (
    <BaseFormSelectField {...rest} name={name} ref={ref} />
  );
}

FormSelectFieldInner.displayName = "FormSelectField";

export const FormSelectField = forwardRef(FormSelectFieldInner) as <T extends FieldValues>(
  props: FormSelectFieldProps<T> & RefAttributes<HTMLDivElement>
) => JSX.Element;

function SubscribedFormSelectFieldInner<T extends FieldValues>(
  {
    control,
    defaultValue,
    name,
    onBlur,
    onChange,
    rules,
    shouldUnregister,
    inputRef,
    ...rest
  }: SubscribedFormSelectFieldProps<T>,
  ref: Ref<HTMLDivElement>
) {
  const validators = useValidators(rules);

  const { field } = useController<T>({
    name,
    control,
    defaultValue: defaultValue as PathValue<T, Path<T>> | undefined,
    rules: validators,
    shouldUnregister,
  });

  const handleChange = useCallback(
    (event: SelectChangeEvent<unknown>, child: React.ReactNode) => {
      onChange?.(event, child);
      field.onChange(event.target.value);
    },
    [field, onChange]
  );

  const handleBlur = useCallback(() => {
    onBlur?.();
    field.onBlur();
  }, [field, onBlur]);

  return (
    <BaseFormSelectField
      {...rest}
      value={field.value ?? ""}
      inputRef={inputRef}
      ref={ref}
      onBlur={handleBlur}
      onChange={handleChange}
      data-test={rest?.["data-test"] || "form-select-field"}
      name={name}
    />
  );
}

const SubscribedFormSelectField = forwardRef(SubscribedFormSelectFieldInner) as <T extends FieldValues>(
  props: SubscribedFormSelectFieldProps<T> & RefAttributes<HTMLDivElement>
) => JSX.Element;

const BaseFormSelectField = forwardRef<HTMLDivElement, BaseFormSelectFieldProps>(
  (
    {
      id,
      inputRef,
      error,
      disabled,
      required,
      components,
      placeholder,
      value,
      defaultValue,
      renderValue,
      "data-test": dataTest,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();

    const renderIcon = useCallback(
      ({ className }: { className: string }) => {
        const open = className.includes("MuiSelect-iconOpen");

        if (value && rest.onClear) {
          return (
            <FormIcon
              size="small"
              sx={{
                position: "absolute",
                top: "50%",
                right: "8px",
                translate: "0% -50%",
                zIndex: 1,
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                rest.onClear?.(e);
              }}
              disabled={disabled}
            >
              <Cross />
            </FormIcon>
          );
        }

        return (
          <FormIcon
            size="medium"
            sx={mergeSx(
              {
                position: "absolute",
                top: "50%",
                right: "8px",
                translate: "0% -50%",
                transition: "rotate 200ms ease-out",
                pointerEvents: "none",
                ...(open && { rotate: "180deg" }),
              },
              components?.icon?.sx
            )}
          >
            <ChevronDown />
          </FormIcon>
        );
      },
      [components?.icon?.sx, disabled, value, rest]
    );

    if (!theme.palette.forms || !theme.palette.formsComponents?.global || !theme.palette.formsComponents?.selectField) {
      return <InvalidThemeErrorMessage />;
    }

    const styles = mergeSx(
      {
        ...theme.typography.body2,
        lineHeight: "26px",
        color: theme.palette.formsComponents.selectField.textColor,
        backgroundColor: theme.palette.formsComponents.selectField.backgroundColor,
        border: `1px solid ${theme.palette.formsComponents.selectField.borderColor}`,
        borderRadius: theme.spacing(1),
        transition: "outline 100ms ease-out, border-color 200ms ease-out",
        outlineColor: "transparent",
        ["&:before, &:after"]: {
          display: "none", // Override default MUI styles
        },
        ["&.Mui-error"]: {
          borderColor: theme.palette.formsComponents.selectField.errorBorderColor,
        },
        ["&:hover:not(.Mui-disabled)"]: {
          borderColor: theme.palette.formsComponents.selectField.hoverBorderColor,
        },
        ["&.Mui-focused"]: {
          borderColor: theme.palette.formsComponents.selectField.hoverBorderColor,
          outline: `3px solid ${theme.palette.formsComponents.global.focusOutlineColor}`,
        },
        ["& .MuiSelect-select"]: {
          display: "flex",
          alignItems: "center",
          padding: theme.spacing(0.75, 0, 0.75, 1),
          pr: "36px !important", // Override default MUI style
          height: "24px !important", // Override default MUI style
          ["&:focus"]: {
            backgroundColor: "transparent !important",
          },
          ["&.Mui-disabled"]: {
            "-webkit-text-fill-color": theme.palette.formsComponents.selectField.textColor, // Override MUI default color
          },
        },
        ["&.Mui-disabled"]: {
          opacity: 0.4,
          color: theme.palette.formsComponents.selectField.textColor, // Override MUI default color
          "-webkit-text-fill-color": theme.palette.formsComponents.selectField.textColor, // Override MUI default color
        },
      },
      components?.select?.sx
    );

    return (
      <FormField
        {...getFormFieldProps(rest)}
        ref={ref}
        labelId={id}
        error={error}
        required={required}
        disabled={disabled}
        data-test={dataTest}
      >
        <Select
          {...rest}
          labelId={id}
          inputRef={inputRef}
          sx={styles}
          error={error}
          disabled={disabled}
          variant="standard"
          placeholder={placeholder}
          IconComponent={renderIcon}
          {...(value !== undefined ? { value } : { defaultValue })}
          renderValue={(value) =>
            value !== undefined && value !== "" ? (
              <FormTypography variant="body2" component="span" sx={{ width: "100%" }}>
                {renderValue?.(value) ?? value}
              </FormTypography>
            ) : (
              <FormTypography
                variant="body2"
                color={theme.palette.formsComponents.selectField.placeholderColor}
                component="span"
                sx={mergeSx({ userSelect: "none", width: "100%" }, components?.placeholder?.sx)}
              >
                {placeholder}
              </FormTypography>
            )
          }
          displayEmpty
          MenuProps={{
            PaperProps: {
              sx: mergeSx(
                {
                  maxHeight: 192, // Show 5.5 elements, so users know they should scroll to see more
                  backgroundColor: theme.palette.formsComponents.selectField.popoverBackgroundColor,
                  scrollbarColor: `${theme.palette.formsComponents.global.scrollbarThumbColor} transparent`,
                  ["&::-webkit-scrollbar-track"]: {
                    background: "transparent",
                  },
                  ["&::-webkit-scrollbar-thumb"]: {
                    background: theme.palette.formsComponents.global.scrollbarThumbColor,
                  },
                },
                components?.menu?.sx
              ),
            },
          }}
        />
      </FormField>
    );
  }
);

BaseFormSelectField.displayName = "BaseFormSelectField";
